// src/components/MemberPanel.js
// The small member panel (evergreen 3.5b team memberships, design 13.2).
//
// What a member has, read from their OWN pair doc (useMyMembership's
// normalized doc: ms fields, never a raw Timestamp): "Member of Team X",
// "Member since {Month YYYY}", the perks they actually have and "Manage
// membership" (-> Your memberships, where billing lives). Shared by the
// public team page (TeamPageScreen) and the squad page (TeamStreamScreen).
// The watch page keeps its own inline channel panel (StreamWatchScreen);
// extracting it is a refactor with no product change, not taken in 3.5b.
//
// Money-free: no price, no date of a charge, no share. The perks come from
// LISTED_PERKS_BY_KIND[kind], so a team panel can never list a videos perk.
// A member the captain removed keeps no team perk (deliveredPerks).
//
// On web, closing the panel hands focus back to the button that opened it
// (returnFocusRef), the 13.13 rule.

import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  Modal,
  StyleSheet,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  deliveredPerks,
  teamDisplayName,
  LISTED_PERKS_BY_KIND,
  PERK_LABELS_BY_KIND,
} from '../utils/membershipPayment';

const COLORS = {
  CARD: '#111',
  CARD_BORDER: '#222',
  GOLD: '#FCD000',
};

// ms -> "September 2026"; '' when absent (the watch page's Member since).
export function memberSinceLabel(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '';
  return new Date(n).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// Focus the element behind a ref on web (a Pressable / TouchableOpacity ref
// is its DOM node there). Quiet everywhere else and on any failure.
export function restoreFocus(ref) {
  if (Platform.OS !== 'web') return;
  const el = ref && ref.current;
  if (!el || typeof el.focus !== 'function') return;
  setTimeout(() => {
    try { el.focus(); } catch (_) { /* the node went away: nothing to focus */ }
  }, 0);
}

// The panel title: the team noun through teamDisplayName (never "Team Team
// Liquid"); a channel keeps the watch page's wording.
export function memberPanelTitle(kind, name) {
  if (kind === 'team') return `Member of ${teamDisplayName(name)}`;
  const n = String(name || '').trim();
  return n ? `Member of ${n}'s channel` : 'Member';
}

// The perk keys this member actually has, in display order.
export function memberPanelPerkKeys(kind, doc) {
  const listed = LISTED_PERKS_BY_KIND[kind] || [];
  if (!doc) return [];
  const have = deliveredPerks(doc.perks || {}, doc.removedByCreator === true) || {};
  return listed.filter((k) => have[k] === true);
}

export default function MemberPanel({
  visible, onClose, kind = 'team', name, doc, navigation, returnFocusRef,
}) {
  const open = visible === true;
  const labels = PERK_LABELS_BY_KIND[kind] || {};
  const perkKeys = memberPanelPerkKeys(kind, doc);
  const since = memberSinceLabel(doc?.memberSinceMs);
  const title = memberPanelTitle(kind, name);

  // Focus goes back to the opener on the open -> closed edge only.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) restoreFocus(returnFocusRef);
    wasOpen.current = open;
  }, [open, returnFocusRef]);

  const close = () => { if (onClose) onClose(); };
  const manage = () => {
    close();
    if (navigation && typeof navigation.navigate === 'function') navigation.navigate('Memberships');
  };

  return (
    <Modal visible={open} transparent animationType="fade" onRequestClose={close}>
      <Pressable style={styles.backdrop} onPress={close}>
        <Pressable style={styles.card} onPress={(e) => e.stopPropagation()} accessibilityViewIsModal>
          <View style={styles.header}>
            <Ionicons name="star" size={18} color={COLORS.GOLD} style={{ marginRight: 8 }} />
            <Text style={styles.title} accessibilityRole="header">{title}</Text>
          </View>
          {since ? <Text style={styles.sub}>Member since {since}</Text> : null}
          {perkKeys.length ? (
            <View style={styles.perkList}>
              {perkKeys.map((k) => (
                <View key={k} style={styles.perkRow}>
                  <Ionicons name="checkmark" size={14} color={COLORS.GOLD} style={{ marginRight: 8 }} />
                  <Text style={styles.perkText}>{labels[k]}</Text>
                </View>
              ))}
            </View>
          ) : null}
          <TouchableOpacity
            style={styles.manageBtn}
            onPress={manage}
            accessibilityRole="button"
            accessibilityLabel="Manage membership"
          >
            <Text style={styles.manageBtnText}>Manage membership</Text>
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// The watch page's modal card idiom (StreamWatchScreen rumbleCard / member
// panel), so the three member panels look alike.
const styles = StyleSheet.create({
  backdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'center', alignItems: 'center', padding: 16,
  },
  card: {
    width: '100%', maxWidth: 380, backgroundColor: COLORS.CARD,
    borderColor: COLORS.CARD_BORDER, borderWidth: 1, borderRadius: 14, padding: 20,
  },
  header: { flexDirection: 'row', alignItems: 'center' },
  title: { color: '#fff', fontSize: 17, fontWeight: '800', flex: 1 },
  sub: { color: '#999', fontSize: 12.5, lineHeight: 18, marginTop: 6 },
  perkList: { marginTop: 12 },
  perkRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 4 },
  perkText: { color: '#ddd', fontSize: 13, flexShrink: 1 },
  manageBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 10, paddingVertical: 12, minHeight: 36,
    alignItems: 'center', justifyContent: 'center', marginTop: 14,
  },
  manageBtnText: { color: '#0b0b0b', fontSize: 14, fontWeight: '800' },
});
